import React, { CSSProperties, FunctionComponent } from 'react';
import ReactSelect, { StylesConfig, ValueType } from 'react-select';
import AsyncSelect from 'react-select/async';

import { getSelectControlCSSProperties, SELECT_CUSTOM_STYLES, SelectOptionType, SelectProps } from './Select.constants';
import InputWrapper from '../InputWrapper';

const Select: FunctionComponent<SelectProps> = ({
  className,
  classNamePrefix,
  defaultOption,
  error,
  id,
  disabled = false,
  isMulti = false,
  isSearchable = false,
  name,
  noOptionsMessage,
  noOptionsText,
  onValueChanged,
  optional,
  options = [],
  placeholder,
  required,
  shrunken = false,
  subTitle,
  title,
  touched,
  valid,
  value,
  verticallyCentered,
  async = false,
  onRequestOptions,
}) => {
  const selectWrapperClasses = ['o-forms-input', 'o-forms-input--select'];

  if (className) {
    selectWrapperClasses.push(className);
  }

  if (error || (touched && !valid)) {
    selectWrapperClasses.push('o-forms-input--invalid');
  }

  const getNoOptionsText = (): string | null => (noOptionsText ? noOptionsText : null);

  const selectStyles: StylesConfig<SelectOptionType, boolean> = {
    ...SELECT_CUSTOM_STYLES,
    control: (provided, state) => ({
      ...provided,
      ...(getSelectControlCSSProperties(
        state.isFocused,
        state.isDisabled,
        !!error || (touched && !valid),
      ) as CSSProperties),
    }),
  };

  const defaultSelectedOption = options
    ? ((options as SelectOptionType[]).find((option) => option.value === defaultOption) as ValueType<
        SelectOptionType,
        boolean
      >)
    : undefined;

  const handleOnChange = (selectedOption: ValueType<SelectOptionType, boolean>): void => {
    name && onValueChanged && onValueChanged(name, selectedOption as SelectOptionType | SelectOptionType[]);
  };

  const props = {
    maxMenuHeight: 200,
    styles: selectStyles,
    classNamePrefix,
    defaultValue: defaultSelectedOption,
    isDisabled: disabled,
    isMulti,
    name,
    placeholder,
    required,
    noOptionsMessage: noOptionsText ? getNoOptionsText : noOptionsMessage,
    value,
    onChange: handleOnChange,
    openMenuOnFocus: true,
    tabSelectsValue: false,
  };

  const syncProps = { ...props, isSearchable, options };
  const asyncProps = {
    ...props,
    cacheOptions: true,
    // defaultOptions: true,
    loadOptions: onRequestOptions || ((): Promise<SelectOptionType[]> => Promise.resolve([])),
  };

  return (
    <InputWrapper
      id={id}
      inline={shrunken}
      optional={optional}
      required={required}
      shrunken={shrunken}
      subTitle={subTitle}
      title={title}
      verticallyCentered={verticallyCentered}
    >
      <div className={selectWrapperClasses.join(' ')}>
        {async ? (
          <AsyncSelect<SelectOptionType, boolean> {...asyncProps} />
        ) : (
          <ReactSelect<SelectOptionType, boolean> {...syncProps} />
        )}
        {error && <span className="o-forms-input__error">{error}</span>}
      </div>
    </InputWrapper>
  );
};

export default Select;
